import "./lib/faro";
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import Web3ModalProvider, { getQueryClient } from "./context/Web3ModalProvider";
import { CryptoIconProvider } from "./components/CryptoIcon";
import { fetchTokenIndexes } from "./services/tokenIndexService";
import { fetchVaultChartData } from "./services/vaultPerformanceService";
import { DEFAULT_TOKEN_INDEX_ID } from "./constants/tokenIndexes";
import App from "./App";


// Initialize Telegram Mini App
const tg = window.Telegram?.WebApp;
if (tg) {
  tg.ready();
  tg.expand();
  tg.setHeaderColor('#1F1F1F');
  tg.setBackgroundColor('#1F1F1F');
}

// ✅ Prefetch token indexes for fast dashboard loading
// Token indexes change rarely (only when adding new portfolios)
// Prefetching here eliminates waterfall delay on Dashboard mount
const queryClient = getQueryClient();

// Prefetch tokenIndexes first, then prefetch vault performance for default portfolio
// This eliminates the waterfall: tokenIndexes → vaultPerformance
queryClient.prefetchQuery({
  queryKey: ['tokenIndexes', true], // enabledOnly = true
  queryFn: () => fetchTokenIndexes(true),
  staleTime: 5 * 60 * 1000, // 5 minutes - matches updated useTokenIndexes hook
}).then(async () => {
  // After tokenIndexes are cached, get the default vault address and prefetch its chart data
  const tokenIndexes = queryClient.getQueryData<Awaited<ReturnType<typeof fetchTokenIndexes>>>(['tokenIndexes', true]);
  const defaultIndex = tokenIndexes?.find(idx => idx.id === DEFAULT_TOKEN_INDEX_ID);

  const vaultAddress = defaultIndex?.vault_address;
  if (vaultAddress) {
    // Prefetch vault performance for the default portfolio (30D is the default view)
    queryClient.prefetchQuery({
      queryKey: ['vaultPerformance', vaultAddress, '30D'],
      queryFn: () => fetchVaultChartData(vaultAddress, '30D'),
      staleTime: 2 * 60 * 1000, // 2 minutes - matches useVaultPerformance hook
    });
  }
}).catch(() => {
  // Silent failure - prefetch is optional optimization
});

const container = document.getElementById("root");
if (!container) throw new Error("Root container missing in index.html");

ReactDOM.createRoot(container).render(
  <React.StrictMode>
    <CryptoIconProvider>
      <Web3ModalProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <App />
        </BrowserRouter>
      </Web3ModalProvider>
    </CryptoIconProvider>
  </React.StrictMode>,
);
